import React from 'react';
import { TCourier } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { useDeclineCourierMutation, useConnectCourierMutation } from '../../queries';
import {
  trackInvitationGuestConnectSuccess,
  trackInvitationGuestDeclineSuccess,
} from '../../tracking/invitation';
import { encryptHash } from '@shared/utils';
import styles from './CourierPendingRequestDemandList.module.scss';
interface componentProps {
  couriers: Array<TCourier>;
}

import { NotificationCategories, useNotificationsActions } from '@src/shared/context/notifications';
import PIENotification, { IPieNotificationMode } from '@src/shared/components/PIENotification';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import PIEToggler from '@src/shared/components/PIEToggler';
import PIEText from '@src/shared/components/PIEText';
import { IconUserGroup } from '@justeattakeaway/pie-icons-react';
import PIEButton from '@src/shared/components/PIEButton';
function CourierPendingRequestDemandList({ couriers = [] }: componentProps) {
  const { notifyInformation, notifyError } = useNotificationsActions(
    NotificationCategories.CourierManagement
  );
  const { t } = useTranslation();

  /* ReactQuery */
  const queryClient = useQueryClient();

  /* Context State */
  const { restaurant } = useKeycloakUserState();

  const declineCourierMutation = useDeclineCourierMutation({
    onSuccess: (data: TCourier) => {
      // TODO Update notifi message
      trackInvitationGuestDeclineSuccess(
        restaurant?.language,
        restaurant?.country,
        encryptHash(data.email)
      );
    },
    onError: () => {
      notifyError(t('notification.commonError'), t('notification_headers.genericErrorHeader'));
    },
    onSettled: () => {
      queryClient.invalidateQueries('couriers');
    },
  });

  const acceptCourierMutation = useConnectCourierMutation({
    onSuccess: (data: TCourier) => {
      trackInvitationGuestConnectSuccess(
        restaurant?.language,
        restaurant?.country,
        encryptHash(data.email)
      );
      notifyInformation(t('manage_couriers.ADD_COURIER_SUCCESS_MESSAGE'), '');
    },
    onError: () => {
      notifyError(t('notification.commonError'), t('notification_headers.genericErrorHeader'));
    },
    onSettled: () => {
      queryClient.invalidateQueries('couriers');
    },
  });

  /* React Query Handlers */
  const onLinkAccepted = async (id: string) => {
    acceptCourierMutation.mutate(id);
  };

  const onLinkDeclined = async (id: string) => {
    declineCourierMutation.mutate(id);
  };

  if (!couriers.length) {
    return null;
  }

  return (
    <div className={styles.pendingInvitationsList}>
      <PIEToggler
        label={
          <div className={styles.togglerHeader}>
            <IconUserGroup size="l" className={styles.headerIcon} />
            <PIEText
              text={`${t('courier.connectionRequestTitleMulti')} (${couriers.length})`}
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-extrabold)"
            />
          </div>
        }
        className={styles.approvalPendingCouriers}
        modFullBorder={false}
        modSeparator={false}
      >
        {couriers.map((courier, index) => {
          return (
            <div key={index}>
              <PIEComponentUtil marginBottom={'var(--xds-spacing-d)'}>
                <div className={styles.pendingCourierItem}>
                  <span className={styles.pendingCourierItemText}>
                    <b>
                      {courier.firstName} {courier.lastName} ({courier.email})
                    </b>{' '}
                    {t('manage_couriers.COURIER_CONNECTION_DEMAND_MESSAGE')}
                  </span>
                  <div className={styles.pendingCourierItemButtonsContainer}>
                    <PIEButton
                      onClick={() => {
                        onLinkDeclined(courier.id);
                      }}
                      size="small"
                      mode="outline"
                      text={t('manage_couriers.COURIER_CONNECTION_DEMAND_MESSAGE_DECLINE_BUTTON')}
                      type="submit"
                      invert={true}
                      qaSelector="courier-page-add-courier-button"
                    />
                    <PIEButton
                      onClick={() => {
                        onLinkAccepted(courier.id);
                      }}
                      size="small"
                      mode="primary"
                      text={t('manage_couriers.COURIER_CONNECTION_DEMAND_MESSAGE_ACCEPT_BUTTON')}
                      type="submit"
                      invert={true}
                      qaSelector="courier-page-add-courier-button"
                    />
                  </div>
                </div>
              </PIEComponentUtil>
            </div>
          );
        })}
      </PIEToggler>
    </div>
  );
}

export default CourierPendingRequestDemandList;
